import {
  type ActionConfig,
  type BreadcrumbItem,
  PageHeader,
} from '@/components/PageHeader';
import { usePersistentState } from '@/utils/hooks';
import Card, { type CardProps } from 'antd/es/card';
import { type PropsWithChildren, type ReactElement } from 'react';
import { css, styled } from 'styled-components';

export interface DetailsPageLayoutProps extends PropsWithChildren {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: ActionConfig[];
  showFullScreenToggle?: boolean;
  breadcrumbs?: BreadcrumbItem[];
}

const Container = styled.div<{ $fullScreen: boolean }>`
  ${({ theme, $fullScreen }) => css`
    max-width: ${$fullScreen ? '100%' : `${theme.screenXL}px`};
    margin: 0 auto;
    transition: all ${theme.motionDurationMid} ease;
    width: ${$fullScreen ? '100%' : 'auto'};
    box-sizing: border-box;
  `}
`;

const TwoColumnLayout = styled.div<{ $hasSideContent: boolean }>`
  ${({ theme, $hasSideContent }) => css`
    .main-column,
    .side-column {
      display: block;
    }

    .single-column {
      display: none;
    }

    display: grid;
    grid-template-columns: ${$hasSideContent ? '2fr 1fr' : '1fr'};
    gap: ${theme.marginLG}px;
    align-items: start;
    transition: all ${theme.motionDurationMid} ease;

    /* will only show one column on mobile or smaller screens (like stripe) */
    @container main-content (max-width: ${theme.screenMD}px) {
      grid-template-columns: 1fr;

      .main-column,
      .side-column {
        display: none;
      }

      .single-column {
        display: block;
      }
    }
  `}
`;

const Section = styled(Card)`
  &.ant-card {
    border: none;
    background: none;
  }
`;

interface ContentProps extends CardProps {}

const MainContent = ({ children, ...props }: ContentProps) => {
  return <Section {...props}>{children}</Section>;
};

const SideContent = ({ children, ...props }: ContentProps) => {
  return <Section {...props}>{children}</Section>;
};

const TopContent = ({ children, ...props }: ContentProps) => {
  return <Section {...props}>{children}</Section>;
};

export const DetailsPageLayout = ({
  children,
  title,
  description,
  actions,
  showFullScreenToggle = true,
  breadcrumbs,
}: DetailsPageLayoutProps) => {
  const [fullScreen, setFullScreen] = usePersistentState('fullScreen', false);

  // Separate main content, side content, and top content, but preserve original order
  const childrenArray = Array.isArray(children) ? children : [children];
  const mainContent: ReactElement[] = [];
  const sideContent: ReactElement[] = [];
  const topContent: ReactElement[] = [];
  const allOrderedChildren: ReactElement[] = [];

  childrenArray.forEach((child) => {
    if (child && typeof child === 'object' && 'type' in child) {
      const element = child as ReactElement;
      if (element.type === MainContent) {
        mainContent.push(element);
        allOrderedChildren.push(element);
      } else if (element.type === SideContent) {
        sideContent.push(element);
        allOrderedChildren.push(element);
      } else if (element.type === TopContent) {
        topContent.push(element);
      }
    }
  });

  return (
    <Container $fullScreen={fullScreen}>
      <PageHeader
        title={title}
        description={description}
        actions={actions}
        showFullScreenToggle={showFullScreenToggle}
        fullScreen={fullScreen}
        onFullScreenToggle={setFullScreen}
        breadcrumbs={breadcrumbs}
      />
      {topContent.length > 0 && <div>{topContent}</div>}
      <TwoColumnLayout $hasSideContent={sideContent.length > 0}>
        <div className="main-column">{mainContent}</div>
        <div className="side-column">{sideContent}</div>
        <div className="single-column">{allOrderedChildren}</div>
      </TwoColumnLayout>
    </Container>
  );
};

DetailsPageLayout.MainContent = MainContent;
DetailsPageLayout.SideContent = SideContent;
DetailsPageLayout.TopContent = TopContent;
